import { getSite } from "_foundation/hooks/useSite";
import {
  HistoricalOrderChannel,
  HistoricalOrderSource,
  HistoricalOrderStatusFilter,
  HistoricalOrderSummaryPage,
  OrderHistoryApi,
} from "@efood-commerce-sdk/typescript-axios-transaction-efood";
import { site as constantsSite } from "_foundationExt/constants/site";
import ensureObject from "tools/ensureObject";
import { AxiosHeaders, AxiosResponse } from "axios";

const orderHistoryApiInstance = new OrderHistoryApi(
  undefined,
  constantsSite.transactionContext
);

const DEFAULT_ORDER_HISTORY_RESPONSE: AxiosResponse<
  HistoricalOrderSummaryPage,
  unknown
> = {
  status: 400,
  statusText: "Bad Request",
  headers: {},
  config: { headers: new AxiosHeaders() },
  data: { entries: [], size: 0 },
};
const orderHistoryService = {
  /**
   * Adds a productlist to the cart with the specified customerProductListId
   * @param parameters {customerProductListId: string}
   */
  addOrderHistoryToCart(parameters: {
    orderHistoryId: string;
    storeId?: string;
  }) {
    const site = getSite();
    const { orderHistoryId, storeId = site?.storeID } =
      ensureObject(parameters);

    if (!orderHistoryId || !storeId) {
      return Promise.reject();
    }

    return orderHistoryApiInstance.addOrderHistoryToCart(
      storeId,
      orderHistoryId
    );
  },

  /**
   * Get order history for a specific product
   * @param parameters {storeId: string, partNumber: string, organizationId?: string}
   */
  getArticleOrderHistory(parameters: { partNumber: string; storeId?: string }) {
    const site = getSite();
    const { storeId = site?.storeID, partNumber } = ensureObject(parameters);

    if (!partNumber || !storeId) {
      return Promise.reject();
    }

    return orderHistoryApiInstance.getArticleOrderHistory(storeId, partNumber);
  },

  /**
   * Get order history details for a specific order
   * @param parameters {storeId: string, orderId: string, source?: HistoricalOrderSource}
   */
  getHistoricalOrder(parameters: {
    orderId: string;
    storeId?: string;
    source?: HistoricalOrderSource;
  }) {
    const site = getSite();
    const {
      orderId,
      storeId = site?.storeID,
      source,
    } = ensureObject(parameters);

    if (!orderId || !storeId) {
      return Promise.reject();
    }

    return orderHistoryApiInstance.getHistoricalOrder(storeId, orderId, source);
  },

  getHistoricalOrderSummaries(parameters: {
    customerOrganizationId: string;
    storeId?: string;
    channel?: HistoricalOrderChannel;
    seller?: string[];
    orderStatus?: HistoricalOrderStatusFilter[];
    dateFrom?: string;
    dateTo?: string;
    pageNumber?: number;
    pageSize?: number;
  }) {
    const site = getSite();
    const {
      storeId = site?.storeID,
      customerOrganizationId,
      channel,
      seller,
      orderStatus,
      dateFrom,
      dateTo,
      pageNumber,
      pageSize,
    } = ensureObject(parameters);

    if (!customerOrganizationId || !storeId) {
      return Promise.resolve(DEFAULT_ORDER_HISTORY_RESPONSE);
    }

    return orderHistoryApiInstance.getHistoricalOrderSummaries(
      storeId,
      customerOrganizationId,
      channel,
      seller,
      orderStatus,
      dateFrom,
      dateTo,
      pageNumber,
      pageSize
    );
  },
};

export default orderHistoryService;
